import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ENV_CONFIG from '../config/environment';
import { AppTheme, DEFAULT_THEME, parseTheme } from '../theme/themes';

export interface AssociationData {
  slug: string;
  name: { ar: string; fr: string; en: string };
  subtitle: { ar: string; fr: string; en: string };
  teamName: { ar: string; fr: string; en: string };
  website: string;
  supportEmail: string;
  defaultEmailSubject: { ar: string; fr: string; en: string };
  defaultLanguage: 'ar' | 'fr' | 'en';
  primaryColor: string;
  logoUrl: string | null;
  theme: AppTheme;
  keywords?: { ar: string; fr: string; en: string };
  ogImage?: string | null;
  /** Fuseau IANA de l'association (paramÃ¨tre `default_timezone`) — sert Ã  l'affichage des dates/heures. */
  timezone: string;
}

export type { AppTheme };

interface AssociationContextType {
  association: AssociationData | null;
  loading: boolean;
  getName: (lang: string) => string;
  getSubtitle: (lang: string) => string;  /** Fuseau IANA de l'association (repli : Africa/Casablanca). */
  timezone: string;  /** Force un re-fetch depuis l'API et met à jour le cache + le DOM. */
  refresh: () => Promise<void>;
}

const CACHE_KEY = 'assoc_cache_v2';

/** Fallback neutre : aucune donnée spécifique à une association. */
const NEUTRAL: AssociationData = {
  slug: '',
  name: { ar: '', fr: '', en: '' },
  subtitle: { ar: '', fr: '', en: '' },
  teamName: { ar: '', fr: '', en: '' },
  website: '',
  supportEmail: '',
  defaultEmailSubject: { ar: '', fr: '', en: '' },
  defaultLanguage: 'ar',
  primaryColor: DEFAULT_THEME.primary,
  logoUrl: null,
  theme: DEFAULT_THEME,
  timezone: 'Africa/Casablanca',
};

function readCache(): AssociationData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AssociationData;
    return {
      ...parsed,
      defaultLanguage: parseDefaultLanguage(parsed.defaultLanguage),
      timezone: normalizeTimezone(parsed.timezone),
    };
  } catch {
    return null;
  }
}

function writeCache(data: AssociationData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore les erreurs de quota/privé
  }
}

function applyTheme(theme: AppTheme): void {
  const r = document.documentElement;
  r.style.setProperty('--theme-primary',      theme.primary);
  r.style.setProperty('--theme-primary-text', theme.primaryText);
  r.style.setProperty('--theme-header-bg',    theme.headerBg);
  r.style.setProperty('--theme-header-text',  theme.headerText);
  r.style.setProperty('--theme-accent',       theme.accent);
  r.style.setProperty('--theme-surface',      theme.surface);
  r.style.setProperty('--theme-text',         theme.textColor   ?? '#1a1a2e');
  r.style.setProperty('--theme-background',   theme.background  ?? '#F5F5F5');
  r.style.setProperty('--theme-border',       theme.borderColor ?? '#E0E0E0');
  // Rétrocompatibilité avec --color-primary
  r.style.setProperty('--color-primary',      theme.primary);
}

/**
 * Met à jour dynamiquement les meta tags HTML et le titre du navigateur.
 * Fonctionne en développement (CRA) et en production (server.js inject déjà les valeurs,
 * mais cette fonction les réactualise après chaque mise à jour admin).
 */
function parseDefaultLanguage(raw: unknown): 'ar' | 'fr' | 'en' {
  let v: unknown = raw;
  if (typeof v === 'string' && /^".*"$/.test(v.trim())) {
    try {
      v = JSON.parse(v);
    } catch {
      /* keep */
    }
  }
  const s = String(v ?? 'ar').toLowerCase().trim();
  return s === 'fr' || s === 'en' ? s : 'ar';
}

/** Fuseau IANA valide, sinon repli sur Africa/Casablanca. */
function normalizeTimezone(raw: unknown): string {
  const v = typeof raw === 'string' ? raw.trim() : '';
  if (!v) return 'Africa/Casablanca';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: v });
    return v;
  } catch {
    return 'Africa/Casablanca';
  }
}

function resolveUiLanguage(data: AssociationData): 'ar' | 'fr' | 'en' {
  try {
    const saved = localStorage.getItem('selectedLanguage');
    if (saved && ['ar', 'fr', 'en'].includes(saved)) {
      return saved as 'ar' | 'fr' | 'en';
    }
  } catch {
    /* ignore */
  }
  return data.defaultLanguage ?? 'ar';
}

function applyMetaTags(data: AssociationData): void {
  const lang = resolveUiLanguage(data);

  const subtitle   = data.subtitle[lang]  || data.subtitle.fr  || data.subtitle.ar  || '';
  const keywords   = data.keywords?.[lang] || '';
  const ogImage    = data.ogImage || data.logoUrl || '';
  const website    = data.website   || '';
  const isRTL      = lang === 'ar';

  // Titre et lang du document
  if (subtitle)          document.title = subtitle;
  document.documentElement.lang = lang;
  document.documentElement.dir  = isRTL ? 'rtl' : 'ltr';

  const setMeta = (selector: string, attr: string, value: string) => {
    if (!value) return;
    const el = document.querySelector(selector);
    if (el) el.setAttribute(attr, value);
  };

  setMeta('meta[name="description"]',          'content', subtitle);
  setMeta('meta[name="keywords"]',             'content', keywords);
  setMeta('meta[name="author"]',               'content', subtitle);
  setMeta('meta[name="theme-color"]',          'content', data.theme?.primary || data.primaryColor || NEUTRAL.primaryColor);
  setMeta('meta[property="og:url"]',           'content', website);
  setMeta('meta[property="og:title"]',         'content', subtitle);
  setMeta('meta[property="og:description"]',   'content', subtitle);
  setMeta('meta[property="og:image"]',         'content', ogImage);
  setMeta('meta[property="twitter:url"]',      'content', website);
  setMeta('meta[property="twitter:title"]',    'content', subtitle);
  setMeta('meta[property="twitter:description"]', 'content', subtitle);
  setMeta('meta[property="twitter:image"]',    'content', ogImage);
}

const AssociationContext = createContext<AssociationContextType>({
  association: null,
  loading: true,
  getName: () => '',
  getSubtitle: () => '',
  timezone: 'Africa/Casablanca',
  refresh: async () => {},
});

export function AssociationProvider({ children }: { children: ReactNode }) {
  // Initialiser depuis le cache local si disponible (évite le flash neutre au 2e chargement)
  const [association, setAssociation] = useState<AssociationData | null>(() => {
    const cached = readCache();
    if (cached) {
      const theme = parseTheme(cached.theme, cached.primaryColor);
      applyTheme(theme);
      applyMetaTags({ ...cached, theme });
    }
    return cached;
  });
  const [loading, setLoading] = useState(true);

  const apiBase = ENV_CONFIG.apiBaseUrl;

  const fetchAndApply = async (): Promise<void> => {
    if (!apiBase) return;
    const r = await fetch(`${apiBase}/api/association`);
    const data = await r.json();
    if (data.success && data.data) {
      const raw = data.data;
      const theme = parseTheme(raw.theme, raw.primaryColor);
      const fresh: AssociationData = {
        ...raw,
        theme,
        defaultLanguage: parseDefaultLanguage(raw.defaultLanguage),
        timezone: normalizeTimezone(raw.timezone),
      };
      setAssociation(fresh);
      writeCache(fresh);
      applyTheme(theme);
      applyMetaTags(fresh);
    }
  };

  useEffect(() => {
    if (!apiBase) { setLoading(false); return; }
    fetchAndApply()
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async (): Promise<void> => {
    try {
      await fetchAndApply();
    } catch {
      // Silencieux — le cache reste actif
    }
  };

  const current = association ?? NEUTRAL;

  const getName = (lang: string): string =>
    current.name[lang as 'ar' | 'fr' | 'en'] ?? current.name.fr ?? '';

  const getSubtitle = (lang: string): string =>
    current.subtitle[lang as 'ar' | 'fr' | 'en'] ?? current.subtitle.fr ?? '';

  return (
    <AssociationContext.Provider value={{ association: current, loading, getName, getSubtitle, refresh, timezone: current.timezone }}>
      {children}
    </AssociationContext.Provider>
  );
}

export function useAssociation(): AssociationContextType {
  return useContext(AssociationContext);
}
